import React, { useState, useEffect } from 'react';

const baseURL = process.env.REACT_APP_API_BASE_URL;

// Skeleton component for table rows
const TableRowSkeleton = ({ columns }) => (
  <tr className="animate-pulse">
    {Array(columns).fill(0).map((_, index) => (
      <td key={index} className="px-6 py-4 whitespace-nowrap">
        <div className="h-4 bg-_gray_lite rounded w-3/4"></div>
      </td>
    ))}
  </tr>
);

// Skeleton component for the orders table
const OrdersTableSkeleton = () => (
  <div className="bg-_white shadow rounded-lg overflow-hidden border border-_gray_lite">
    <div className="px-6 py-4 border-b border-_gray_lite">
      <div className="h-6 bg-_gray_lite rounded w-40 animate-pulse"></div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-_gray_hover">
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Order ID</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-_gray_lite">
          {Array(5).fill(0).map((_, index) => (
            <TableRowSkeleton key={index} columns={4} />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Skeleton component for the customers table
const CustomersTableSkeleton = () => (
  <div className="bg-_white shadow rounded-lg overflow-hidden border border-_gray_lite">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-_gray_lite">
        <thead className="bg-_gray_hover">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">ID</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Phone</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        <tbody className="bg-_white divide-y divide-_gray_lite">
          {Array(8).fill(0).map((_, index) => (
            <TableRowSkeleton key={index} columns={5} />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Customers = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState({ users: false, orders: false });
  const [error, setError] = useState({ users: null, orders: null });

  // Function to get token from sessionStorage
  const getToken = () => {
    const token = sessionStorage.getItem('token');
    return token;
  };

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(prev => ({ ...prev, users: true }));
      setError(prev => ({ ...prev, users: null }));
      
      try {
        const token = getToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const response = await fetch(`${baseURL}/api/users/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error fetching users: ${response.statusText}`);
        }
        
        const data = await response.json();
        setUsers(data);
      } catch (err) {
        setError(prev => ({ ...prev, users: err.message }));
        console.error('Failed to fetch users:', err);
      } finally {
        setLoading(prev => ({ ...prev, users: false }));
      }
    };
    
    fetchUsers();
  }, []);

  // Fetch user orders when a user is selected
  useEffect(() => {
    if (!selectedUser) return;
    
    const fetchOrders = async () => {
      setLoading(prev => ({ ...prev, orders: true }));
      setError(prev => ({ ...prev, orders: null }));
      
      try {
        const token = getToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const response = await fetch(`${baseURL}/api/users/order/${selectedUser.userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error fetching orders: ${response.statusText}`);
        }
        
        const data = await response.json();
        setOrders(data.data);
      } catch (err) {
        setError(prev => ({ ...prev, orders: err.message }));
        console.error('Failed to fetch orders:', err);
      } finally {
        setLoading(prev => ({ ...prev, orders: false }));
      }
    };
    
    fetchOrders();
  }, [selectedUser]);

  const handleUserClick = (user) => {
    setSelectedUser(user);
  };

  const handleBackClick = () => {
    setSelectedUser(null);
    setOrders([]);
  };

  // Render orders view
  if (selectedUser) {
    return (
      <div className="space-y-4 max-w-6xl mx-auto p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-_dark">
            Orders for {selectedUser.name || selectedUser.firstName + " " + selectedUser.lastName || selectedUser.email || `Customer #${selectedUser.userId}`}
          </h1>
          <button 
            onClick={handleBackClick} 
            className="px-4 py-2 bg-_gray_hover hover:bg-_gray_lite rounded border border-_gray text-_dark"
          >
            Back to Customers
          </button>
        </div>
        
        {error.orders && (
          <div className="flex items-center p-4 mb-4 bg-red-100 text-_red rounded border border-_red">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p>{error.orders}</p>
          </div>
        )}
        
        {loading.orders ? (
          <OrdersTableSkeleton />
        ) : orders?.length > 0 ? (
          <div className="bg-_white shadow rounded-lg overflow-hidden border border-_gray_lite">
            <div className="px-6 py-4 border-b border-_gray_lite">
              <h2 className="font-semibold text-lg text-_dark">Order History</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-_gray_hover">
                    <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-_gray_lite">
                  {orders?.map((order) => (
                    <tr key={order.id} className="hover:bg-_gray_hover">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-_dark">{order.orderId}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-_gray">
                        {new Date(order.date || order.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          order.orderStatus === 'completed' ? 'bg-green-100 text-green-800' :
                          order.orderStatus === 'processing' ? 'bg-blue-100 text-_blue' :
                          order.orderStatus === 'cancelled' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-_gray_lite text-_gray'
                        }`}>
                          {order.orderStatus || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-_gray">
                        LKR {order?.amount || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center p-8 bg-_gray_hover rounded-lg border border-_gray_lite">
            <p className="text-_gray">No orders found for this customer</p>
          </div>
        )}
      </div>
    );
  }

  // Render users view
  return (
    <div className="space-y-4 max-w-6xl mx-auto p-4">
      <h1 className="text-2xl font-bold text-_dark">Customers</h1>
      
      {error.users && (
        <div className="flex items-center p-4 mb-4 bg-red-100 text-_red rounded border border-_red">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p>{error.users}</p>
        </div>
      )}
      
      {loading.users ? (
        <CustomersTableSkeleton />
      ) : users.length > 0 ? (
        <div className="bg-_white shadow rounded-lg overflow-hidden border border-_gray_lite">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-_gray_lite">
              <thead className="bg-_gray_hover">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-_dark_lite uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-_white divide-y divide-_gray_lite">
                {users.map((user) => (
                  <tr key={user.userId} className="hover:bg-_gray_hover">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-_dark">{user.userId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-_gray">{user.firstName + " " + user.lastName || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-_gray">{user.email || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-_gray">{user.phone || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleUserClick(user)} 
                        className="text-_blue hover:text-_bg_lite px-3 py-1 bg-_gray_hover hover:bg-_gray_lite rounded"
                      >
                        View Orders
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center p-8 bg-_gray_hover rounded-lg border border-_gray_lite">
          <p className="text-_gray">No customers found</p>
        </div>
      )}
    </div>
  );
};

export default Customers;